import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { DocumentStatus } from '../types/document';
import toast from 'react-hot-toast';

/**
 * Types for the document download function
 */
export interface DocumentToDownload {
  pilgrimId: string;
  pilgrimName: string;
  documentId: string;
  documentType: 'passport' | 'visa';
  status: 'complete' | 'missing';
  fileUrl?: string;
  passportNumber?: string;
}

export interface DownloadAllOptions {
  documents: DocumentToDownload[];
  documentStatuses: Record<string, DocumentStatus>;
  documentTypeFilter: 'all' | 'passport' | 'visa';
  translations: {
    downloadSuccess: string;
    downloadError: string;
  };
}

/**
 * Function to download all documents as a zip file with parallel processing
 */
export const downloadAllAsZip = async ({
  documents,
  documentStatuses,
  documentTypeFilter,
  translations
}: DownloadAllOptions): Promise<boolean> => {
  try {
    console.log('Downloading documents with filter:', documentTypeFilter);
    
    // Determine which document types to include based on filter
    const docTypesToInclude: ('passport' | 'visa')[] = 
      documentTypeFilter === 'all' 
        ? ['passport', 'visa'] 
        : [documentTypeFilter];
    
    console.log(`📁 Document types to include: ${docTypesToInclude.join(', ')}`);
    
    // Filter documents to download
    const documentsToDownload = documents.filter(doc => 
      doc.status === 'complete' && 
      docTypesToInclude.includes(doc.documentType) &&
      documentStatuses[doc.documentId]?.fileUrl
    );
    
    if (documentsToDownload.length === 0) {
      toast.error('No documents available to download');
      return false;
    }

    // Show initial progress
    toast.loading(`Preparing ${documentsToDownload.length} documents for download...`, { id: 'download-progress' });

    // Create a new zip file
    const zip = new JSZip();
    
    // Create folders for each document type
    const folders: Record<string, JSZip> = {};
    docTypesToInclude.forEach(type => {
      folders[type] = zip.folder(type) || zip;
    });

    // Process documents in batches of 5 to avoid overwhelming the browser
    const BATCH_SIZE = 5;
    let successCount = 0;
    let errorCount = 0;

    // Process in batches
    for (let i = 0; i < documentsToDownload.length; i += BATCH_SIZE) {
      const batch = documentsToDownload.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(async (doc) => {
        const docStatus = documentStatuses[doc.documentId];
        if (!docStatus || !docStatus.fileUrl) return { success: false, doc };

        try {
          console.log(`➕ Adding ${doc.documentType} for pilgrim: ${doc.pilgrimName}`);
          
          // Fetch the file
          const response = await fetch(docStatus.fileUrl);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.statusText}`);
          }
          
          // Get file as blob
          const blob = await response.blob();
          
          // Determine filename - make it more unique
          const sanitizedName = doc.pilgrimName.replace(/\s+/g, '_').replace(/[^a-z0-9_]/gi, '');
          const fileName = `${sanitizedName}_${doc.passportNumber}_${doc.documentType}.${docStatus.fileType === 'pdf' ? 'pdf' : 'jpg'}`;
          
          // Add file to zip in the appropriate folder
          folders[doc.documentType].file(fileName, blob);
          return { success: true, doc };
        } catch (error) {
          console.error(`Error adding file for ${doc.pilgrimName}:`, error);
          return { success: false, doc, error };
        }
      });

      // Wait for all promises in this batch to resolve
      const results = await Promise.all(batchPromises);
      
      // Count successes and failures
      results.forEach(result => {
        if (result.success) successCount++;
        else errorCount++;
      });

      // Update progress toast
      const progress = Math.round(((i + batch.length) / documentsToDownload.length) * 100);
      toast.loading(`Processing documents: ${progress}% complete...`, { id: 'download-progress' });
    }

    if (successCount === 0) {
      toast.dismiss('download-progress');
      throw new Error('No documents were successfully added to the zip file.');
    }

    // Update toast to indicate zip generation
    toast.loading('Generating zip file...', { id: 'download-progress' });

    // Generate and download the zip file
    const content = await zip.generateAsync({
      type: 'blob',
      compression: 'DEFLATE',
      compressionOptions: { level: 6 }
    });

    // Generate filename with date
    const date = new Date().toISOString().split('T')[0];
    const typeLabel = documentTypeFilter === 'all' ? 'all' : documentTypeFilter;
    const filename = `pilgrim-documents-${typeLabel}-${date}.zip`;
    
    // Save the zip file
    saveAs(content, filename);

    // Dismiss progress toast and show success message
    toast.dismiss('download-progress');
    const message = errorCount > 0
      ? `Downloaded ${successCount} documents (${errorCount} failed)`
      : translations.downloadSuccess;

    toast.success(message);
    return true;
  } catch (error) {
    toast.dismiss('download-progress');
    console.error('Error creating zip file:', error);
    toast.error(translations.downloadError);
    return false;
  }
};
